/**
 * Fase 2.5 T2.5.2 — FinalizeAssessmentModal
 *
 * Sensor/admin bruker denne for å finalisere kompositt-vurdering per kandidat.
 * Viser komponent-sammendrag + foreslått karakter fra vekting for sammensatt vurdering.
 * Krever K11-rationale hvis sensor avviker fra foreslått score.
 * Krever K10-habilitet-bekreftelse hvis sensor er hybrid.
 */
import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, X, Award, AlertTriangle } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

interface Props {
  candidateId: string;
  onClose: () => void;
  onFinalized?: () => void;
}

interface Composite {
  model: string;
  plan_id: string;
  calculated_score: number | null;
  calculated_passed: boolean | null;
  total_components: number;
  graded_components: number;
  missing_components: number;
  ready_to_finalize: boolean;
  error?: string;
}

interface Submissions {
  candidate_id: string;
  submissions: Array<{
    submission_id: string;
    component_id: string;
    component_type: string;
    component_title: string;
    component_sequence: number;
    status: string;
    sensor_score: number | null;
    sensor_grade: string | null;
    sensor_passed: boolean | null;
    alternative_assessment_used: boolean | null;
  }>;
  final_assessment: any;
}

export default function FinalizeAssessmentModal({ candidateId, onClose, onFinalized }: Props) {
  const [composite, setComposite] = useState<Composite | null>(null);
  const [submissions, setSubmissions] = useState<Submissions | null>(null);
  const [sensorCtx, setSensorCtx] = useState<{ is_hybrid: boolean; requires_habilitet_confirmation: boolean } | null>(null);

  const [overallGrade, setOverallGrade] = useState('');
  const [overallPassed, setOverallPassed] = useState(true);
  const [overallScore, setOverallScore] = useState<string>('');
  const [sensorRationale, setSensorRationale] = useState('');
  const [habilitetConfirmed, setHabilitetConfirmed] = useState(false);
  const [habilitetReason, setHabilitetReason] = useState('');
  const [alternativeScope, setAlternativeScope] = useState('none');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await authenticatedFetch(`/api/admin/final-assessments/${candidateId}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        setComposite(j.composite);
        setSubmissions(j.submissions);
        setSensorCtx(j.sensor_context);

        // Preset overallScore fra calculated_score
        if (j.composite?.calculated_score != null) {
          setOverallScore(String(j.composite.calculated_score));
          setOverallPassed(j.composite.calculated_passed !== false);
        }
        // Preset fra eksisterende final_assessment
        if (j.submissions?.final_assessment?.overall_grade) {
          setOverallGrade(j.submissions.final_assessment.overall_grade);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'unknown');
      } finally {
        setLoading(false);
      }
    })();
  }, [candidateId]);

  const scoreValue = overallScore === '' ? null : Number(overallScore);
  const scoreDiverges = composite?.calculated_score != null && scoreValue != null
    && Math.abs(scoreValue - Number(composite.calculated_score)) >= 1;

  async function submit() {
    if (!overallGrade.trim()) { setError('Karakter må fylles inn'); return; }
    if (scoreDiverges && sensorRationale.trim().length < 30) {
      setError('Sensor må begrunne avvik fra foreslått score (min 30 tegn)');
      return;
    }
    if (sensorCtx?.requires_habilitet_confirmation) {
      if (!habilitetConfirmed) { setError('Bekreft habilitet-erklæring'); return; }
      if (habilitetReason.trim().length < 20) { setError('Habilitet-begrunnelse må ha minst 20 tegn'); return; }
    }
    setSaving(true);
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/final-assessments/${candidateId}/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          overall_grade: overallGrade.trim(),
          overall_passed: overallPassed,
          overall_score: scoreValue,
          sensor_rationale: sensorRationale.trim() || null,
          habilitet_confirmed: habilitetConfirmed,
          habilitet_reason: habilitetReason.trim() || null,
          alternative_scope: alternativeScope
        })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || err.error || `HTTP ${r.status}`);
      }
      if (onFinalized) onFinalized();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full p-4 sm:p-6 max-h-[95vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Finaliser vurdering</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>

        {loading && <div className="p-4 text-gray-600">Laster…</div>}
        {error && !composite && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
            <AlertCircle className="h-4 w-4 inline mr-1" /> {error}
          </div>
        )}

        {composite && submissions && (
          <>
            {!composite.ready_to_finalize && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900">
                <div className="flex items-center gap-2 font-medium mb-1"><AlertTriangle className="h-4 w-4" /> Ikke klar til finalisering</div>
                {composite.graded_components} av {composite.total_components} komponenter er sensurert.
                Alle må sensureres først.
              </div>
            )}

            <div className="mb-4">
              <div className="text-sm font-medium text-gray-900 mb-2">Komponenter</div>
              <div className="space-y-1">
                {submissions.submissions.map(s => (
                  <div key={s.submission_id} className="flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className={s.status === 'graded' || s.status === 'overridden_by_alternative' ? 'text-emerald-600' : 'text-gray-400'}>
                        {s.status === 'graded' || s.status === 'overridden_by_alternative' ? '✓' : '○'}
                      </span>
                      <span className="text-gray-900">{s.component_title || s.component_type}</span>
                    </div>
                    <div className="text-xs text-gray-600">
                      {s.sensor_score != null ? `${s.sensor_score}` : '—'}
                      {s.sensor_grade && ` (${s.sensor_grade})`}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {composite.calculated_score != null && (
              <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-kf-blue-800 font-medium">Foreslått score fra vekting</div>
                    <div className="text-2xl font-bold text-kf-blue-900">{Number(composite.calculated_score).toFixed(1)}</div>
                  </div>
                  <Award className={`h-10 w-10 ${composite.calculated_passed ? 'text-emerald-600' : 'text-red-600'}`} />
                </div>
              </div>
            )}

            {composite.ready_to_finalize && (
              <>
                <div className="space-y-3 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Karakter (påkrevd)</label>
                    <input value={overallGrade} onChange={e => setOverallGrade(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="F.eks. «B», «Bestått», «80%»" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Score</label>
                      <input type="number" min={0} max={100} step={0.1} value={overallScore} onChange={e => setOverallScore(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Resultat</label>
                      <select value={overallPassed ? 'y' : 'n'} onChange={e => setOverallPassed(e.target.value === 'y')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                        <option value="y">Bestått</option>
                        <option value="n">Ikke bestått</option>
                      </select>
                    </div>
                  </div>
                  {scoreDiverges && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sensor-begrunnelse for avvik fra foreslått score (min 30 tegn)
                      </label>
                      <textarea value={sensorRationale} onChange={e => setSensorRationale(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Forklar hvorfor endelig score avviker fra vekting-beregningen…" />
                      <div className="text-xs text-gray-500 mt-1">{sensorRationale.length} / min 30 tegn</div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Alternativ vurdering</label>
                    <select value={alternativeScope} onChange={e => setAlternativeScope(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                      <option value="none">Ingen (normal vurdering)</option>
                      <option value="single_component">Én komponent erstattet av alternativ vurdering</option>
                      <option value="all">All vurdering erstattet av alternativ (muntlig/observasjon)</option>
                      <option value="partial_recognition">Delvis realkompetanse-anerkjennelse</option>
                    </select>
                  </div>
                </div>

                {sensorCtx?.requires_habilitet_confirmation && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <div className="text-sm font-medium text-amber-900 mb-2">
                      <AlertTriangle className="h-4 w-4 inline mr-1" /> Habilitet-erklæring kreves
                    </div>
                    <p className="text-xs text-amber-800 mb-2">
                      Du er hybrid — medlem/administrator i samme organisasjon som kurset. For å finalisere må du bekrefte at du har vurdert habiliteten din faglig og etisk.
                    </p>
                    <label className="flex items-start gap-2 text-sm text-amber-900 mb-2">
                      <input type="checkbox" checked={habilitetConfirmed} onChange={e => setHabilitetConfirmed(e.target.checked)} className="mt-0.5" />
                      <span>Jeg bekrefter at jeg har vurdert kandidaten faglig upartisk til tross for hybrid-tilknytning.</span>
                    </label>
                    <textarea value={habilitetReason} onChange={e => setHabilitetReason(e.target.value)} rows={2} className="w-full border border-amber-300 rounded-md px-3 py-2 text-sm" placeholder="Kort begrunnelse (min 20 tegn) — hvorfor er habiliteten din OK i dette tilfellet?" />
                    <div className="text-xs text-amber-800 mt-1">{habilitetReason.length} / min 20 tegn — loggføres i audit-trail</div>
                  </div>
                )}
              </>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800 mb-3">
                <AlertCircle className="h-4 w-4 inline mr-1" /> {error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Avbryt</button>
              {composite.ready_to_finalize && (
                <button
                  onClick={submit}
                  disabled={saving}
                  className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" /> {saving ? 'Finaliserer…' : 'Finaliser vurdering'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
